import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import Datetime from "react-datetime";

import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles } from "@material-ui/core/styles";
import Assignment from "@material-ui/icons/Assignment";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import { bookingApi } from "functions/apis";
import { formatDateTime, token } from "functions/Helper";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px"
  }
};

const useStyles = makeStyles(styles);

export default function List() {
  const source = useRef(new AbortController());

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [filter, setFilter] = React.useState({
    lookUpCode: null,
    minEffectiveDate: null,
    maxEffectiveDate: null,
    currentActive: false
  });
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    setLoading(true);
    bookingApi
      .get("/TFSFees", {
        signal: source.current.signal,
        params: {
          filter: {
            lookUpCode: filter.lookUpCode === "" ? null : filter.lookUpCode,
            minEffectiveDate: filter.minEffectiveDate,
            maxEffectiveDate: filter.maxEffectiveDate,
            currentActive: filter.currentActive
          },
          pagination
        }
      })
      .then(resp => {
        setData(
          resp.records.map(record => ({
            ...record,
            actions: (
              <div className="actions-right">
                {(token["booking.tfs_fees.write"] === "allowed" ||
                  token.is_admin === "True") && (
                  <Link to={"/admin/TFSFees/Edit/" + record.id}>
                    <Button color="info" className="remove">
                      <EditIcon /> EDIT
                    </Button>
                  </Link>
                )}
                {(token["booking.tfs_fees.write"] === "allowed" ||
                  token.is_admin === "True") && (
                  <Link to={"/admin/TFSFees/Delete/" + record.id}>
                    <Button color="danger" className="remove">
                      <DeleteIcon /> DELETE
                    </Button>
                  </Link>
                )}
              </div>
            )
          }))
        );
        setPages(Math.ceil(resp.totalRecords / pagination.pageSize));
      })
      .catch(error => console.log(error))
      .finally(() => setLoading(false));
  }, [pagination, filter]);

  const handleLookUpCodeChanged = e => {
    const value = e?.target?.value ?? "";
    setFilter(prev => ({
      ...prev,
      lookUpCode: value === "" ? null : value
    }));
  };

  const handleMinEffectiveDateChanged = value =>
    setFilter(prev => ({
      ...prev,
      minEffectiveDate: value === "" ? null : value
    }));

  const handleMaxEffectiveDateChanged = value =>
    setFilter(prev => ({
      ...prev,
      maxEffectiveDate: value === "" ? null : value
    }));

  const handleCurrentActiveChanged = e =>
    setFilter(prev => ({
      ...prev,
      currentActive: e?.target?.checked ?? false
    }));

  const classes = useStyles();

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>TFS Fees</h4>
          </CardHeader>
          <CardBody>
            {(token["booking.tfs_fees.write"] === "allowed" ||
              token.is_admin === "True") && (
              <Link to="/admin/TFSFees/Create">
                <Button color="rose" type="button">
                  Create TFS Fee
                </Button>
              </Link>
            )}
            <GridContainer>
              <GridItem xs={12} sm={4} md={4}>
                <CustomInput
                  labelText="Look Up Code"
                  formControlProps={{
                    fullWidth: true
                  }}
                  inputProps={{
                    onChange: handleLookUpCodeChanged,
                    value: filter.lookUpCode ?? ""
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4}>
                <FormControl fullWidth>
                  <Datetime
                    value={filter.minEffectiveDate}
                    onChange={handleMinEffectiveDateChanged}
                    timeFormat={false}
                    inputProps={{
                      placeholder: "Min Effective Date"
                    }}
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4} md={4}>
                <FormControl fullWidth>
                  <Datetime
                    value={filter.maxEffectiveDate}
                    onChange={handleMaxEffectiveDateChanged}
                    timeFormat={false}
                    inputProps={{
                      placeholder: "Max Effective Date"
                    }}
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={filter.currentActive}
                      onChange={handleCurrentActiveChanged}
                    />
                  }
                  label="Current Active"
                />
              </GridItem>
            </GridContainer>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  id: "effectiveDate",
                  Header: "Effective Date",
                  accessor: d => formatDateTime(new Date(d.effectiveDate + "Z"))
                },
                {
                  id: "lookUpCode",
                  Header: "Look Up Code",
                  accessor: d => d.lookUpCode
                },
                {
                  id: "idr",
                  Header: "IDR",
                  accessor: d => d.idr.toLocaleString()
                },
                {
                  id: "sgd",
                  Header: "SGD",
                  accessor: d => d.sgd.toLocaleString()
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
